import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { MessageSquare } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import { getAccount, type AccountDetail } from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";
import { usePreapprovalSubproductId } from "@/lib/usePreapprovalSubproduct";
import { AccountMessageForm } from "@/components/AccountMessageForm";
import { SuretyAccountInfo } from "@/components/SuretyAccountInfo";
import { SuretyProgramBlock } from "@/components/SuretyProgramBlock";
import { readPreapprovalApplication } from "@/lib/preapprovalApplication";
import { DocumentsPanel } from "@/components/surety-account/SuretyAccountPanels";
import { BondHistoryPanel } from "@/components/surety-account/BondHistoryPanel";
import { SuretyOwnersPanel } from "@/components/surety-account/SuretyOwnersPanel";
import { AccountInvoicesPanel } from "@/components/AccountInvoicesPanel";
import { AccountFinancialsPanel } from "@/components/AccountFinancialsPanel";
import { readAdvanceRequirements } from "@/lib/signalAdvanceEvidence";
import { readCapacityRequest } from "@/lib/capacityRequest";
import { SignalAdvanceApplication } from "@/components/SignalAdvanceApplication";
import { PageHeader } from "@/components/PageHeader";
import { ACCOUNT_MESSAGE_TOPICS } from "@/lib/accountMessage";
import { SuretyAccountActions } from "@/components/surety-account/SuretyAccountActions";
import { RecordTabs } from "@/base/RecordTabs";
import { recordPanelId, recordTabId, useTabParam } from "@/base/tabParam";

// BRK-S138 — the Surety account PROFILE: one insured, as the bond side reads
// them.
//
// A SECOND VIEW of the same record, not a second record. `/accounts/{id}` is the
// Commercial-side card — the whole editable account, personnel and phones and
// two addresses. This page answers the questions a bond underwriter asks first:
// who is this insured, and what standing program do they hold (BRK-S139). Both
// read the SAME own-scoped `GET /api/broker/accounts/{id}`; PMS is the system of
// record and there is no surety-specific account (NN #9).
//
// The v2 shell (BRK-S398): a header with the insured's name, "Message us about
// this account" (a dialog) and an Actions menu, over SEVEN tabs held in
// `?tab=` — see TABS below for what each hosts until its own story lands.
//
// It lives under `/surety` so the platform is derived from the route
// (BRK-S090) — a broker who deep-links here stays in Surety, where the
// Commercial `/accounts` prefix is deliberately line-agnostic.
export function SuretyAccountPage() {
  const { id = "" } = useParams();
  const { identity, authedRequest } = useAuth();
  const canView = hasCapability(identity, "account:view");
  const canEdit = hasCapability(identity, "account:edit");

  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped to re-read the account after an evidence upload (BRK-S330), so the
  // requirement chips are PMS's derivation rather than a local guess.
  const [readVersion, setReadVersion] = useState(0);
  // Which subproduct the "Get pre-approved" CTA links to (BRK-S160). `null`
  // while the catalog read is in flight, and `null` for good when the catalog
  // carries no standalone subproduct — both render today's CTA.
  const preapprovalSubproductId = usePreapprovalSubproductId();

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await getAccount(authedRequest, id);
        if (!cancelled) setDetail(fresh);
      } catch (err) {
        // A 404 covers "no such account" AND "not your brokerage's" — the
        // own-scoping is server-side and deliberately indistinguishable, so
        // this renders PMS's own sentence rather than guessing which (NN #7).
        if (!cancelled) setLoadError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id, canView, readVersion]);

  const messageTitleId = useId();
  const [messageOpen, setMessageOpen] = useState(false);

  // The standing PROGRAM (BRK-S139), on BOTH Company profile and Surety
  // program, as the wireframe draws it. One element, so the two cannot differ.
  const program = detail && (
    <SuretyProgramBlock
      accountId={detail.id}
      preapprovalSubproductId={preapprovalSubproductId}
      // The current pre-approval application (BRK-S324, PMS-S719).
      application={readPreapprovalApplication(detail.preapprovalApplication)}
      capacityRequest={readCapacityRequest(detail.capacityRequest)}
      onCapacityChanged={() => setReadVersion((n) => n + 1)}
      // The Signal Advance evidence (BRK-S330): both requirements met on an
      // Express program reads "Signal Advance · In review" (BRK-S401).
      advance={readAdvanceRequirements(detail.advanceRequirements)}
      preapproval={detail.preapproval}
      // The program record's Advance limits (BRK-S501): the Advance
      // popover's "up to" figures.
      advanceLimits={detail.advanceLimits}
    />
  );

  return (
    <>
      {/* The insured's name is the heading, and the account number sits under
          it — the two things a broker reads out to Signal. */}
      <PageHeader
        title={detail ? detail.legalName : "Account"}
        meta={detail && `Account ${detail.accountNumber}`}
        actions={
          detail && (
            <>
              {/* Filing a message is a write on the account's record, so it
                  carries the same gate the Commercial card applies. */}
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<MessageSquare size={16} aria-hidden />}
                  onClick={() => setMessageOpen(true)}
                  sx={{ bgcolor: "background.paper", fontWeight: 600 }}
                >
                  Message us about this account
                </Button>
              )}
              <SuretyAccountActions detail={detail} />
            </>
          )
        }
      />
      <section className="flex flex-col gap-6">
        {!canView && (
          <p role="alert" className="text-sm text-brk-danger">
            You don’t have access to view this account.
          </p>
        )}

        {loadError && (
          <p role="alert" className="text-sm text-brk-danger">
            {loadError}
          </p>
        )}

        {canView && !detail && !loadError && (
          <p role="status" className="text-sm text-brk-dim">
            Loading account…
          </p>
        )}

        {detail && (
          <SuretyAccountTabs
            panels={{
              company: (
                <>
                  <SuretyAccountInfo
                    detail={detail}
                    canEdit={canEdit}
                    onUpdated={setDetail}
                  />
                  {program}
                  {/* The wireframe's Company profile ends with the Bond
                      history table — the same list the Bond history tab
                      shows (BRK-S501). No Required steps panel and no
                      capacity-request notice on the account page: the GIA's
                      state lives on the Owners tab's "Details needed" flags
                      and the submission's GIA Form button. */}
                  <BondHistoryPanel accountId={detail.id} />
                </>
              ),
              program: (
                <>
                  {program}
                  {/* The Signal Advance application, `#advance-app`
                      (BRK-S407): steps to enroll, the client link rows and the
                      document alternative (BRK-S330) — the program block's
                      "Enroll in Signal Advance" lands here. */}
                  <SignalAdvanceApplication
                    detail={detail}
                    onRefreshed={setDetail}
                    onChanged={() => setReadVersion((v) => v + 1)}
                  />
                </>
              ),
              history: <BondHistoryPanel accountId={detail.id} />,
              owners: (
                <SuretyOwnersPanel
                  detail={detail}
                  canEdit={canEdit}
                  onUpdated={setDetail}
                />
              ),
              financials: <AccountFinancialsPanel detail={detail} />,
              billing: <AccountInvoicesPanel accountId={detail.id} />,
              documents: <DocumentsPanel accountId={detail.id} />,
            }}
          />
        )}

        <Link
          to="/surety/accounts"
          className="text-sm text-brk-accent hover:underline"
        >
          ← Back to accounts
        </Link>
      </section>

      {/* "Message us about this account" (BRK-S159, BRK-S176): the SAME
          composer and route the Commercial card uses — a second composer would
          be a second set of validation rules for one Communication Log entry.
          In a dialog from the header since BRK-S398, with a Topic that rides in
          the subject. */}
      {detail && canEdit && (
        <Dialog
          open={messageOpen}
          onClose={() => setMessageOpen(false)}
          fullWidth
          maxWidth="sm"
          aria-labelledby={messageTitleId}
        >
          <DialogTitle id={messageTitleId}>
            Message us about this account
          </DialogTitle>
          <DialogContent>
            <AccountMessageForm
              accountId={detail.id}
              topics={ACCOUNT_MESSAGE_TOPICS}
              framed={false}
              onCancel={() => setMessageOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/**
 * The seven tabs, in the wireframe's order (BRK-S398). Until each tab's own
 * story lands it hosts today's content, so nothing the page showed is lost:
 *
 * - Company profile: the account-info block, the program, the Bond history
 *   table (BRK-S501).
 * - Surety program: the program again, and the Signal Advance application
 *   (BRK-S407, `#advance-app`).
 * - Bond history: one list over the insured's bond submissions, the bonds
 *   Signal issued folded into their rows (BRK-S406, BondHistoryPanel).
 * - Owners & principals: the owners and their spouses (BRK-S408).
 * - Financials: the insured's financial summary, or "No financials yet"
 *   (BRK-S409, AccountFinancialsPanel).
 * - Billing: the invoices. Documents: the account's documents.
 *
 * Quotes and Renewals were "coming soon" tabs on the old row and are gone: the
 * design has no place for them.
 */
const TABS = [
  { id: "company", label: "Company profile" },
  { id: "program", label: "Surety program" },
  { id: "history", label: "Bond history" },
  { id: "owners", label: "Owners & principals" },
  { id: "financials", label: "Financials" },
  { id: "billing", label: "Billing" },
  { id: "documents", label: "Documents" },
] as const;

type TabId = (typeof TABS)[number]["id"];

/**
 * The tab row, in `?tab=` so a reload or a shared link reopens the same tab
 * (the default, Company profile, stays out of the URL).
 *
 * A panel mounts the first time its tab opens and then STAYS mounted, hidden,
 * like the account record's (BaseForm): switching back never loses what was
 * typed or re-reads the panel. Mounting on first visit rather than all at once
 * keeps each tab's reads (orders, bonds, invoices, documents) to the brokers
 * who open it, as the old row did.
 */
function SuretyAccountTabs({ panels }: { panels: Record<TabId, ReactNode> }) {
  const idBase = useId();
  const [active, select] = useTabParam(TABS, "tab");
  const [visited, setVisited] = useState<ReadonlySet<string>>(
    () => new Set([active]),
  );
  // A tab opened from the URL (back/forward, a link) counts as visited too.
  useEffect(() => {
    setVisited((v) => (v.has(active) ? v : new Set(v).add(active)));
  }, [active]);

  return (
    <div className="flex min-w-0 flex-col">
      {/* Scrolls inside its own box at 375px rather than wrapping or pushing
          the page sideways (RecordTabs). */}
      <div className="flex min-w-0 border-b border-brk-border">
        <RecordTabs
          tabs={TABS}
          active={active}
          onSelect={select}
          idBase={idBase}
          ariaLabel="Account sections"
        />
      </div>
      {TABS.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={recordPanelId(idBase, t.id)}
          aria-labelledby={recordTabId(idBase, t.id)}
          hidden={t.id !== active}
          // `hidden` alone loses to `flex` (a utility outranks preflight's
          // `[hidden]` rule), which left a visited tab showing under the
          // active one: the display class follows the state (BRK-S456).
          className={`${t.id === active ? "flex" : "hidden"} mt-4 min-w-0 flex-col gap-6`}
        >
          {(t.id === active || visited.has(t.id)) && panels[t.id]}
        </div>
      ))}
    </div>
  );
}
